import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import adminApi from '../../../services/adminApi';
import CampaignPreviewModal from './CampaignPreviewModal';
import { StatusPill } from './AdminCampaigns';
import {
    EMPTY_CAMPAIGN, EMPTY_AUDIENCE, COMMON_TIME_ZONES, CAMPAIGN_TYPE_LABELS, campaignToForm, formToPayload, formsEqual, browserTimeZone,
    zonedTimeToUtc, formatDateTime, relativeTime, formatPct, fieldErrorsOf, blockingErrors, missingLinksError, testStatusLabel, validationOf,
} from './campaignUtils';
import styles from './AdminCampaigns.module.css';

const MAX_HERO_BYTES = 3 * 1024 * 1024;
const CTA_URL_INPUT_ID = 'campaign-cta-url';
const HERO_LINK_SUGGESTION = "Consider linking your hero image to the page you're promoting.";
const HERO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const LABELS = {
    coins: { any: 'Any', zero: 'Zero coins', has: 'Has coins' },
    foundingCollector: { any: 'Any', only: 'Founding Collectors only', exclude: 'Exclude Founding Collectors' },
    onboarding: { any: 'Any', completed: 'Completed', not_completed: 'Not completed' },
    emailVerified: { any: 'Any', verified: 'Verified', unverified: 'Unverified' },
    signupMethods: { email: 'Email', google: 'Google', apple: 'Apple' },
    platforms: { web: 'Web', ios: 'iOS', android: 'Android' },
    sources: { tiktok: 'TikTok', instagram: 'Instagram', threads: 'Threads', youtube: 'YouTube', x: 'X', direct: 'Direct', other: 'Other' },
};

// ── Small field helpers ─────────────────────────────────────────────────────

// `group` fields (chip sets, upload controls, read-only values) render as a
// div: wrapping them in a <label> would nest labels and misroute clicks.
function Field({ label, error, help, counter, group = false, children }) {
    const Tag = group ? 'div' : 'label';
    return (
        <Tag className={styles.field}>
            <span className={styles.label}>
                <span>{label}</span>
                {counter && <span className={styles.counter}>{counter}</span>}
            </span>
            {children}
            {help && <span className={styles.help}>{help}</span>}
            {error && <span className={styles.fieldError} role="alert">{error}</span>}
        </Tag>
    );
}

function ChipGroup({ options, labels, value, onChange, disabled, name }) {
    const toggle = (opt) => onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
    return (
        <div className={styles.checkboxGroup} role="group" aria-label={name}>
            {options.map((opt) => {
                const key = typeof opt === 'object' ? opt.id : opt;
                const text = typeof opt === 'object' ? opt.name : (labels?.[opt] || opt);
                const active = value.includes(key);
                return (
                    <label key={key} className={`${styles.chip} ${active ? styles.chipActive : ''}`}>
                        <input type="checkbox" checked={active} disabled={disabled} onChange={() => toggle(key)} />
                        {text}
                    </label>
                );
            })}
        </div>
    );
}

// ── Audience ────────────────────────────────────────────────────────────────

function AudienceEditor({ audience, options, onChange, disabled }) {
    if (!options) return null;
    const set = (key, value) => onChange({ ...audience, [key]: value });
    const select = (key, label) => (
        <Field label={label}>
            <select className={styles.select} value={audience[key] ?? 'any'} disabled={disabled} onChange={(e) => set(key, e.target.value)}>
                {options[key].map((o) => <option key={o} value={o}>{LABELS[key][o] || o}</option>)}
            </select>
        </Field>
    );
    return (
        <>
            <div className={styles.fieldRow}>
                {select('coins', 'Coins')}
                <Field label="Activity" help="Last active = most recent session refresh or verified quiz.">
                    <select
                        className={styles.select}
                        value={audience.inactiveDays ?? ''}
                        disabled={disabled}
                        onChange={(e) => set('inactiveDays', e.target.value ? Number(e.target.value) : null)}
                    >
                        <option value="">Any</option>
                        {options.inactiveDays.map((d) => <option key={d} value={d}>Inactive {d}+ days</option>)}
                    </select>
                </Field>
            </div>
            <div className={styles.fieldRow}>
                {select('foundingCollector', 'Founding Collectors')}
                {select('onboarding', 'Onboarding')}
            </div>
            <div className={styles.fieldRow}>
                <Field label="Joined from">
                    <input type="date" className={styles.input} value={audience.joinedFrom || ''} disabled={disabled} onChange={(e) => set('joinedFrom', e.target.value || null)} />
                </Field>
                <Field label="Joined to">
                    <input type="date" className={styles.input} value={audience.joinedTo || ''} disabled={disabled} onChange={(e) => set('joinedTo', e.target.value || null)} />
                </Field>
            </div>
            {select('emailVerified', 'Email verified')}
            {audience.emailVerified === 'unverified' && (
                <p className={styles.help}>Unverified addresses never receive product email, so this segment is always empty.</p>
            )}
            <Field group label="Signup method" help="Accounts created before signup tracking have no method and only match when this is empty.">
                <ChipGroup name="Signup method" options={options.signupMethods} labels={LABELS.signupMethods} value={audience.signupMethods} disabled={disabled} onChange={(v) => set('signupMethods', v)} />
            </Field>
            <Field group label="Signup platform">
                <ChipGroup name="Signup platform" options={options.platforms} labels={LABELS.platforms} value={audience.platforms} disabled={disabled} onChange={(v) => set('platforms', v)} />
            </Field>
            <Field group label="Acquisition source">
                <ChipGroup name="Acquisition source" options={options.sources} labels={LABELS.sources} value={audience.sources} disabled={disabled} onChange={(v) => set('sources', v)} />
            </Field>
            <Field group label="Genre interest" help="Genres the user picked during onboarding.">
                <ChipGroup name="Genre interest" options={options.genres} value={audience.genreIds} disabled={disabled} onChange={(v) => set('genreIds', v)} />
            </Field>
        </>
    );
}

function useAudienceEstimate(audience, enabled) {
    const [state, setState] = React.useState({ loading: false, data: null, error: '' });
    const key = JSON.stringify(audience);
    React.useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        setState((s) => ({ ...s, loading: true, error: '' }));
        const t = setTimeout(async () => {
            try {
                const data = await adminApi.estimateCampaignAudience(JSON.parse(key));
                if (!cancelled) setState({ loading: false, data, error: '' });
            } catch (err) {
                if (!cancelled) setState({ loading: false, data: null, error: err.message || 'Estimate failed' });
            }
        }, 400);
        return () => { cancelled = true; clearTimeout(t); };
    }, [key, enabled]);
    return state;
}

// ── Pre-send validation ─────────────────────────────────────────────────────
// Every rule lives on the server (campaignValidation). The UI only displays
// the server's report, so what the editor shows is exactly what schedule /
// send enforce.

/** Debounced validation of the current (possibly unsaved) form. */
function useCampaignValidation(campaignId, form, enabled) {
    const [state, setState] = React.useState({ loading: false, data: null, error: '' });
    const key = JSON.stringify(formToPayload(form));
    React.useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        setState((s) => ({ ...s, loading: true, error: '' }));
        const t = setTimeout(async () => {
            try {
                const { validation } = await adminApi.validateCampaign(campaignId, JSON.parse(key));
                if (!cancelled) setState({ loading: false, data: validation, error: '' });
            } catch (err) {
                // Field-level problems (e.g. a placeholder URL) come back as 422s.
                if (!cancelled) setState({ loading: false, data: null, error: err.message || 'Checks could not run.' });
            }
        }, 500);
        return () => { cancelled = true; clearTimeout(t); };
    }, [campaignId, key, enabled]);
    return state;
}

/** "Return to Editor": jump to the button URL field so a link can be added. */
function focusCtaField() {
    const el = document.getElementById(CTA_URL_INPUT_ID);
    if (!el) return;
    el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    el.focus();
}

function MissingLinksAlert({ issue, onReturnToEditor }) {
    return (
        <div className={`${styles.errorBanner} ${styles.missingLinks}`} role="alert" data-testid="missing-links">
            <p className={styles.missingLinksTitle}>{issue.message}</p>
            <p className={styles.missingLinksText}>{issue.detail}</p>
            <button type="button" className={styles.secondaryBtn} onClick={onReturnToEditor}>Return to Editor</button>
        </div>
    );
}

function CheckList({ errors = [], warnings = [], testId }) {
    if (!errors.length && !warnings.length) return null;
    return (
        <ul className={styles.checkList} data-testid={testId}>
            {errors.map((e, i) => (
                <li key={`e${i}`} className={`${styles.checkItem} ${styles.checkError}`}>
                    <span className={styles.checkIcon} aria-hidden="true">✕</span><span>{e.message}</span>
                </li>
            ))}
            {warnings.map((w, i) => (
                <li key={`w${i}`} className={`${styles.checkItem} ${styles.checkWarn}`}>
                    <span className={styles.checkIcon} aria-hidden="true">!</span><span>{w.message}</span>
                </li>
            ))}
        </ul>
    );
}

/** Sidebar panel: live result of the pre-send checks for the current edits. */
function PreSendChecks({ state }) {
    const report = state.data;
    const missing = missingLinksError(report);
    const errors = blockingErrors(report).filter((e) => e.code !== 'no_promotional_links');
    return (
        <div className={styles.card} data-testid="presend-checks">
            <h2 className={styles.cardTitle}>Pre-send checks</h2>
            <p className={styles.help}>Checks the email recipients will get: links, plain text, unsubscribe and sender. Link formats are checked — pages are not visited.</p>
            {!report && state.loading && <p className={styles.muted}>Checking…</p>}
            {!report && state.error && <p className={styles.fieldError}>{state.error}</p>}
            {missing && <div style={{ marginTop: 10 }}><MissingLinksAlert issue={missing} onReturnToEditor={focusCtaField} /></div>}
            {report && <CheckList errors={errors} warnings={report.warnings} testId="presend-issues" />}
            {report && !blockingErrors(report).length && !report.warnings.length && (
                <p className={`${styles.checkOk} ${styles.testResult}`}>✓ No problems found</p>
            )}
            {report && (
                <p className={styles.help} style={{ marginTop: 8 }}>
                    {CAMPAIGN_TYPE_LABELS[report.campaignType]} · {report.summary.promotionalLinkCount} destination link{report.summary.promotionalLinkCount === 1 ? '' : 's'}
                </p>
            )}
        </div>
    );
}

// ── Pre-send checklist (Send Now / Schedule) ────────────────────────────────

function PreSendChecklistModal({ campaign, mode, scheduleLabel, busy, serverReport, onCancel, onConfirm, onReturnToEditor }) {
    const [report, setReport] = React.useState(serverReport || null);
    const [estimate, setEstimate] = React.useState(null);
    const [error, setError] = React.useState('');
    const [acknowledged, setAcknowledged] = React.useState(false);

    React.useEffect(() => {
        adminApi.estimateCampaignAudience(campaign.audience)
            .then(setEstimate)
            .catch((err) => setError(err.message || 'Could not count recipients.'));
        // Validate what is saved — that is what will be sent.
        adminApi.validateCampaign(campaign.id, {})
            .then(({ validation }) => setReport(validation))
            .catch((err) => setError(err.message || 'Pre-send checks could not run.'));
    }, [campaign]);

    // A schedule / send rejected by the server comes back with its report.
    React.useEffect(() => { if (serverReport) { setReport(serverReport); setAcknowledged(false); } }, [serverReport]);

    const count = estimate?.eligible;
    const errors = report?.errors || [];
    const warnings = report?.warnings || [];
    const missing = missingLinksError(report);
    const s = report?.summary;
    const sending = mode === 'send';
    const canConfirm = Boolean(report) && !busy && !errors.length && (!warnings.length || acknowledged) && Boolean(count);
    const titleId = 'presend-title';
    const heroStatus = !s ? '…' : { none: 'No hero image', linked: 'Linked', not_linked: 'Not linked' }[s.heroImage];

    return (
        <div className={styles.modalOverlay} onClick={busy ? undefined : onCancel}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={(e) => e.stopPropagation()}>
                <h2 id={titleId} className={styles.modalTitle}>{sending ? 'Send campaign?' : 'Schedule campaign?'}</h2>
                <p><strong>{campaign.name}</strong></p>
                {!report && !error && <p className={styles.muted}>Checking the email…</p>}
                {s && (
                    <dl className={styles.metaList} data-testid="presend-summary">
                        <dt>Type</dt><dd>{CAMPAIGN_TYPE_LABELS[report.campaignType]}</dd>
                        <dt>Subject</dt><dd>{s.subject || '—'}</dd>
                        <dt>Preview text</dt><dd>{s.previewText || <span className={styles.checkWarn}>Missing</span>}</dd>
                        <dt>Audience</dt><dd>{estimate?.summary || campaign.audienceSummary}</dd>
                        <dt>Hero image</dt><dd>{heroStatus}{s.heroLinkUrl && <> → <span className={styles.urlText}>{s.heroLinkUrl}</span></>}</dd>
                        <dt>Button text</dt><dd>{s.ctaText || '—'}</dd>
                        <dt>Button URL</dt><dd>{s.ctaUrl ? <span className={styles.urlText}>{s.ctaUrl}</span> : '—'}</dd>
                        <dt>Destination links</dt><dd>{s.promotionalLinkCount} <span className={styles.muted}>(format checked, not visited)</span></dd>
                        <dt>Compliance</dt>
                        <dd>
                            Unsubscribe link {s.unsubscribe.html && s.unsubscribe.text && s.unsubscribe.header ? '✓ in email, plain text and List-Unsubscribe header' : '✕ missing'}
                            <br />From {s.sender.fromName} &lt;{s.sender.fromEmail}&gt;
                        </dd>
                        <dt>Test email</dt>
                        <dd>{testStatusLabel(s.test)}{s.test.lastSentTo && <span className={styles.muted}> · last sent to {s.test.lastSentTo}</span>}</dd>
                    </dl>
                )}
                {schedulePart(mode, scheduleLabel)}
                <p className={styles.muted} style={{ marginTop: 16 }}>Eligible recipients</p>
                <div className={styles.confirmCount} data-testid="confirm-count">
                    {count === undefined ? '…' : count.toLocaleString()}
                </div>
                {error && <div className={styles.errorBanner}>{error}</div>}
                {missing && <MissingLinksAlert issue={missing} onReturnToEditor={onReturnToEditor} />}
                {errors.filter((e) => e.code !== 'no_promotional_links').length > 0 && (
                    <div className={styles.checklistSection}>
                        <p className={styles.checklistSectionTitle}>Fix before sending</p>
                        <CheckList errors={errors.filter((e) => e.code !== 'no_promotional_links')} testId="presend-errors" />
                    </div>
                )}
                {warnings.length > 0 && (
                    <div className={styles.checklistSection}>
                        <p className={styles.checklistSectionTitle}>Warnings</p>
                        <CheckList warnings={warnings} testId="presend-warnings" />
                        {!errors.length && (
                            <label className={styles.ackRow}>
                                <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} disabled={busy} />
                                <span>I&apos;ve reviewed these warnings and want to continue.</span>
                            </label>
                        )}
                    </div>
                )}
                <p className={styles.confirmWarning}>
                    {sending ? 'This action cannot be undone once sending begins.' : 'The email is checked again automatically when it starts sending.'}
                </p>
                <div className={styles.modalActions}>
                    <button type="button" className={styles.secondaryBtn} onClick={onCancel} disabled={busy}>Cancel</button>
                    <button
                        type="button"
                        className={sending ? styles.dangerBtn : styles.primaryBtn}
                        onClick={() => onConfirm(warnings.map((w) => w.code))}
                        disabled={!canConfirm}
                    >
                        {confirmLabel({ sending, busy, count, scheduleLabel })}
                    </button>
                </div>
            </div>
        </div>
    );
}

function schedulePart(mode, scheduleLabel) {
    if (mode !== 'schedule') return null;
    return <p style={{ marginTop: 12 }}>Scheduled for <strong>{scheduleLabel}</strong></p>;
}

function confirmLabel({ sending, busy, count, scheduleLabel }) {
    if (sending) {
        if (busy) return 'Starting…';
        return `Send to ${count === undefined ? '…' : count.toLocaleString()} users`;
    }
    return busy ? 'Scheduling…' : `Schedule for ${scheduleLabel}`;
}

// ── Detail (non-draft) ──────────────────────────────────────────────────────

function Metric({ label, value, pct, na, sub }) {
    return (
        <div className={styles.metric}>
            <div className={styles.metricLabel}>{label}</div>
            {na ? <div className={styles.metricNA}>Not available</div> : (
                <>
                    <div className={styles.metricValue}>{(value ?? 0).toLocaleString()}</div>
                    {(pct !== null && pct !== undefined) && <div className={styles.metricSub}>{formatPct(pct)}</div>}
                    {sub && <div className={styles.metricSub}>{sub}</div>}
                </>
            )}
        </div>
    );
}

function CampaignReport({ campaign }) {
    const m = campaign.metrics || {};
    const noFeedback = !m.providerFeedback;
    return (
        <div className={styles.card}>
            <h2 className={styles.cardTitle}>Delivery</h2>
            <p className={styles.cardHint}>
                <strong>Queued</strong> = handed to the Aluvo email queue.
                {noFeedback
                    ? ' Delivery, open, click and bounce data require provider feedback from the email service, which is not connected yet — they are not estimated.'
                    : ' Sent / delivered / opened / clicked come from the email provider.'}
            </p>
            <div className={styles.metricsGrid}>
                <Metric label="Recipients" value={m.recipients} />
                <Metric label="Queued" value={m.queued} pct={m.queuedPct} />
                <Metric label="Sent to provider" value={m.sentToProvider} na={noFeedback} />
                <Metric label="Delivered" value={m.delivered} pct={m.deliveredPct} na={noFeedback} />
                <Metric label="Opened" value={m.opened} pct={m.openedPct} na={noFeedback} />
                <Metric
                    label="Clicked"
                    value={m.clicked}
                    pct={m.clickedPct}
                    na={noFeedback}
                    sub={m.clicksByElement ? `Button ${m.clicksByElement.cta} · Image ${m.clicksByElement.hero} · Links ${m.clicksByElement.link}` : undefined}
                />
                <Metric label="Bounced" value={m.bounced} pct={m.bouncedPct} na={noFeedback} />
                <Metric label="Unsubscribed" value={m.unsubscribed} pct={m.unsubscribedPct} />
                <Metric label="Failed to queue" value={m.failed} />
                <Metric label="Skipped" value={m.skipped} sub="opted out / ineligible at send time" />
            </div>
        </div>
    );
}

/** Who unsubscribed through this campaign's email, newest first. */
function CampaignUnsubscribes({ campaignId, count }) {
    const [state, setState] = React.useState({ rows: [], page: 0, totalPages: 0, total: 0, loading: false, error: '' });
    const load = React.useCallback(async (page) => {
        setState((s) => ({ ...s, loading: true, error: '' }));
        try {
            const data = await adminApi.listCampaignUnsubscribes(campaignId, { page });
            setState((s) => ({
                rows: page === 1 ? data.unsubscribes : [...s.rows, ...data.unsubscribes],
                page,
                totalPages: data.pagination.totalPages,
                total: data.pagination.total,
                loading: false,
                error: '',
            }));
        } catch (err) {
            setState((s) => ({ ...s, loading: false, error: err.message || 'Could not load unsubscribes.' }));
        }
    }, [campaignId]);
    // Reload when the count changes (the report refreshes while sending).
    React.useEffect(() => { load(1); }, [load, count]);

    return (
        <div className={styles.card} data-testid="campaign-unsubscribes">
            <h2 className={styles.cardTitle}>Unsubscribed ({state.total.toLocaleString()})</h2>
            <p className={styles.cardHint}>
                People who used the Unsubscribe link in this email. &ldquo;Opted back in&rdquo; means they have turned product email on again in their settings since.
            </p>
            {state.error && <div className={styles.errorBanner}>{state.error}</div>}
            {!state.error && state.rows.length === 0 && !state.loading && <p className={styles.muted}>Nobody has unsubscribed from this campaign.</p>}
            {state.rows.length > 0 && (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr><th>User</th><th>Email</th><th>Unsubscribed</th><th>Now</th></tr>
                        </thead>
                        <tbody>
                            {state.rows.map((u) => (
                                <tr key={u.userId}>
                                    <td>{u.accountDeleted ? <span className={styles.muted}>Deleted account</span> : <>@{u.username}{u.displayName ? <span className={styles.muted}> · {u.displayName}</span> : null}</>}</td>
                                    <td>{u.email || '—'}</td>
                                    <td className={styles.nowrap}>{formatDateTime(u.unsubscribedAt)}</td>
                                    <td className={styles.nowrap}>{u.stillUnsubscribed ? 'Unsubscribed' : 'Opted back in'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {state.page < state.totalPages && (
                <div className={styles.actions} style={{ marginTop: 12 }}>
                    <button type="button" className={styles.secondaryBtn} onClick={() => load(state.page + 1)} disabled={state.loading}>
                        {state.loading ? 'Loading…' : 'Load more'}
                    </button>
                </div>
            )}
        </div>
    );
}

function CampaignDetail({ campaign, onPreview, onDuplicate, onUnschedule, onCancel, busy }) {
    return (
        <>
            {campaign.status === 'SCHEDULED' && (
                <div className={styles.infoBanner}>
                    Scheduled for <strong>{formatDateTime(campaign.scheduledAt, campaign.scheduledTimezone)}</strong>
                    {' '}({campaign.scheduledTimezone}) · your time {formatDateTime(campaign.scheduledAt)}.
                    It will be sent by the server even if nobody is signed in.
                </div>
            )}
            {campaign.status === 'SENDING' && (
                <div className={styles.warningBanner}>Sending in progress — emails are being queued in batches. This page refreshes automatically.</div>
            )}
            {campaign.status === 'FAILED' && (
                <div className={styles.errorBanner}>This campaign failed: {campaign.failureReason || 'unknown error'}.</div>
            )}
            <div className={styles.actions} style={{ marginBottom: 20 }}>
                <button type="button" className={styles.secondaryBtn} onClick={onPreview}>Preview</button>
                <button type="button" className={styles.secondaryBtn} onClick={onDuplicate} disabled={busy}>Duplicate as new draft</button>
                {campaign.status === 'SCHEDULED' && (
                    <>
                        <button type="button" className={styles.secondaryBtn} onClick={onUnschedule} disabled={busy}>Move back to draft</button>
                        <button type="button" className={styles.dangerBtn} onClick={onCancel} disabled={busy}>Cancel campaign</button>
                    </>
                )}
            </div>
            {['SENDING', 'SENT', 'FAILED'].includes(campaign.status) && <CampaignReport campaign={campaign} />}
            {['SENDING', 'SENT', 'FAILED'].includes(campaign.status) && (
                <CampaignUnsubscribes campaignId={campaign.id} count={campaign.metrics?.unsubscribed ?? 0} />
            )}
            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Campaign</h2>
                <dl className={styles.metaList} style={{ marginTop: 12 }}>
                    <dt>Name</dt><dd>{campaign.name}</dd>
                    <dt>Type</dt><dd>{CAMPAIGN_TYPE_LABELS[campaign.campaignType] || 'Promotional'}</dd>
                    <dt>Subject</dt><dd>{campaign.subject}</dd>
                    {campaign.previewText && (<><dt>Preview text</dt><dd>{campaign.previewText}</dd></>)}
                    <dt>From</dt><dd>{campaign.fromName} &lt;{campaign.fromEmail}&gt;</dd>
                    {campaign.replyTo && (<><dt>Reply-to</dt><dd>{campaign.replyTo}</dd></>)}
                    <dt>Audience</dt><dd>{campaign.audienceSummary}</dd>
                    <dt>Created by</dt><dd>{campaign.creator?.username || '—'}</dd>
                    <dt>Created</dt><dd>{formatDateTime(campaign.createdAt)}</dd>
                    {campaign.scheduledAt && (<><dt>Scheduled</dt><dd>{formatDateTime(campaign.scheduledAt, campaign.scheduledTimezone)}</dd></>)}
                    {campaign.sendingStartedAt && (<><dt>Sending started</dt><dd>{formatDateTime(campaign.sendingStartedAt)}</dd></>)}
                    {campaign.sentAt && (<><dt>Sent</dt><dd>{formatDateTime(campaign.sentAt)}</dd></>)}
                    {campaign.cancelledAt && (<><dt>Cancelled</dt><dd>{formatDateTime(campaign.cancelledAt)}</dd></>)}
                    <dt>Recipients</dt><dd>{campaign.recipientCount.toLocaleString()}</dd>
                    {campaign.lastTestSentAt && (<><dt>Last test</dt><dd>{formatDateTime(campaign.lastTestSentAt)} to {campaign.lastTestSentTo}</dd></>)}
                </dl>
            </div>
            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Content</h2>
                <p className={styles.cardHint}>Locked — sent and scheduled campaigns can't be edited. Duplicate to make changes.</p>
                {campaign.headline && <h3 style={{ margin: '0 0 12px' }}>{campaign.headline}</h3>}
                <p className={styles.contentBlock}>{campaign.body}</p>
                {campaign.ctaText && <p className={styles.contentBlock}><strong>{campaign.ctaText}</strong> → {campaign.ctaUrl}</p>}
                {campaign.heroImageUrl && <p className={styles.contentBlock}>Hero image {campaign.heroLinkUrl ? <>→ {campaign.heroLinkUrl}</> : '(not linked)'}</p>}
                {campaign.secondaryText && <p className={styles.contentBlock}>{campaign.secondaryText}</p>}
            </div>
        </>
    );
}

// ── Page ────────────────────────────────────────────────────────────────────

/** /admin/campaigns/new and /admin/campaigns/:id */
export default function AdminCampaignEditor() {
    const { id: routeId } = useParams();
    const isNew = !routeId || routeId === 'new';
    const navigate = useNavigate();
    const location = useLocation();

    const [config, setConfig] = React.useState(null);
    const [campaign, setCampaign] = React.useState(null);
    const [form, setForm] = React.useState(EMPTY_CAMPAIGN);
    const [savedForm, setSavedForm] = React.useState(EMPTY_CAMPAIGN);
    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState('');
    const [errors, setErrors] = React.useState({});
    const [banner, setBanner] = React.useState(null);
    const [saving, setSaving] = React.useState(false);
    const [testing, setTesting] = React.useState(false);
    const [testResult, setTestResult] = React.useState(null);
    const [busy, setBusy] = React.useState(false);
    const [preview, setPreview] = React.useState(null);
    // Pre-send checklist: { mode: 'send' | 'schedule', serverReport } while open.
    const [checklist, setChecklist] = React.useState(null);
    const [sending, setSending] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [schedule, setSchedule] = React.useState({ date: '', time: '09:00', timezone: browserTimeZone() });
    const [, forceTick] = React.useState(0);
    const fileRef = React.useRef(null);
    // Id of the campaign already held in state — saving a new draft replaces
    // the URL (/new → /:id) and must not trigger a reload that drops UI state.
    const loadedIdRef = React.useRef(null);

    const campaignId = campaign?.id || (isNew ? null : routeId);
    const editable = isNew || campaign?.readiness?.editable;
    const dirty = editable && !formsEqual(form, savedForm);

    // Load config + campaign
    React.useEffect(() => {
        if (!isNew && loadedIdRef.current === routeId) return undefined;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setLoadError('');
            try {
                const cfg = await adminApi.getCampaignConfig();
                if (cancelled) return;
                setConfig(cfg);
                if (isNew) {
                    const initial = { ...EMPTY_CAMPAIGN, fromName: cfg.defaultFromName || 'Aluvo', fromEmail: cfg.fromEmail || '', replyTo: cfg.defaultReplyTo || '', audience: { ...EMPTY_AUDIENCE } };
                    setCampaign(null);
                    setForm(initial);
                    setSavedForm(initial);
                } else {
                    const { campaign: c } = await adminApi.getCampaign(routeId);
                    if (cancelled) return;
                    loadedIdRef.current = c.id;
                    setCampaign(c);
                    setForm(campaignToForm(c));
                    setSavedForm(campaignToForm(c));
                }
            } catch (err) {
                if (!cancelled) setLoadError(err.statusCode === 404 ? 'Campaign not found.' : (err.message || 'Could not load the campaign.'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [routeId, isNew]);

    // Scroll to the send panel when arriving from the list's "Schedule / Send".
    React.useEffect(() => {
        if (!loading && location.hash === '#send') document.getElementById('send')?.scrollIntoView({ behavior: 'smooth' });
    }, [loading, location.hash]);

    // Poll while sending so counts / status stay live.
    React.useEffect(() => {
        if (campaign?.status !== 'SENDING') return undefined;
        const t = setInterval(async () => {
            try { setCampaign((await adminApi.getCampaign(campaign.id)).campaign); } catch { /* keep last state */ }
        }, 3000);
        return () => clearInterval(t);
    }, [campaign?.status, campaign?.id]);

    // Keep "Sent just now" fresh.
    React.useEffect(() => {
        if (!testResult?.ok) return undefined;
        const t = setInterval(() => forceTick((n) => n + 1), 30000);
        return () => clearInterval(t);
    }, [testResult]);

    // Warn before leaving with unsaved edits.
    React.useEffect(() => {
        if (!dirty) return undefined;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [dirty]);

    const estimate = useAudienceEstimate(form.audience, Boolean(config) && editable);
    const validation = useCampaignValidation(campaignId, form, Boolean(config) && editable);

    const set = (key) => (e) => {
        const value = e && e.target ? e.target.value : e;
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((errs) => ({ ...errs, [key]: undefined }));
    };

    const applyCampaign = (c) => {
        loadedIdRef.current = c.id;
        setCampaign(c);
        const f = campaignToForm(c);
        setForm(f);
        setSavedForm(f);
    };

    /** Save (create or update). Returns the saved campaign or null. */
    const save = async ({ quiet = false } = {}) => {
        setSaving(true);
        setErrors({});
        if (!quiet) setBanner(null);
        try {
            const payload = formToPayload(form);
            const result = campaignId
                ? await adminApi.updateCampaign(campaignId, payload)
                : await adminApi.createCampaign(payload);
            applyCampaign(result.campaign);
            if (!campaignId) navigate(`/admin/campaigns/${result.campaign.id}`, { replace: true });
            if (!quiet) setBanner({ type: 'success', text: 'Draft saved.' });
            return result.campaign;
        } catch (err) {
            setErrors(fieldErrorsOf(err));
            setBanner({ type: 'error', text: err.message || 'Could not save the campaign.' });
            return null;
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async () => {
        setTesting(true);
        setTestResult(null);
        setBanner(null);
        try {
            let target = campaign;
            if (!campaignId || dirty) {
                target = await save({ quiet: true });
                if (!target) {
                    setTestResult({ ok: false, message: 'Fix the highlighted fields, then send the test again.' });
                    return;
                }
            }
            const result = await adminApi.sendCampaignTest(target.id);
            setTestResult({ ok: true, to: result.sentTo, at: result.sentAt });
            const { campaign: fresh } = await adminApi.getCampaign(target.id);
            setCampaign(fresh);
        } catch (err) {
            setErrors(fieldErrorsOf(err));
            const detail = err.message || '';
            setTestResult({
                ok: false,
                message: err.statusCode === 429 || detail.startsWith('Test email could not be sent')
                    ? detail
                    : `Test email could not be sent. ${detail}`.trim(),
            });
        } finally {
            setTesting(false);
        }
    };

    const uploadHero = async (file) => {
        if (!file) return;
        if (!HERO_TYPES.includes(file.type)) { setErrors((e) => ({ ...e, heroImageUrl: 'Use a JPEG, PNG, WebP or GIF image.' })); return; }
        if (file.size > MAX_HERO_BYTES) { setErrors((e) => ({ ...e, heroImageUrl: 'Image must be 3 MB or smaller.' })); return; }
        setUploading(true);
        try {
            let target = campaign;
            if (!campaignId || dirty) {
                target = await save({ quiet: true });
                if (!target) return;
            }
            const result = await adminApi.uploadCampaignHeroImage(target.id, file, form.heroImageAlt);
            // Keep any other unsaved edits; only the image fields come from the server.
            setCampaign(result.campaign);
            setForm((f) => ({ ...f, heroImageUrl: result.campaign.heroImageUrl || '', heroImageAlt: result.campaign.heroImageAlt || f.heroImageAlt }));
            setSavedForm(campaignToForm(result.campaign));
        } catch (err) {
            setErrors((e) => ({ ...e, heroImageUrl: err.message || 'Upload failed.' }));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const scheduledUtc = zonedTimeToUtc(schedule.date, schedule.time, schedule.timezone);

    /** A rejected schedule / send that carries a validation report keeps the checklist open on it. */
    const handleRejected = (err, fallback) => {
        const report = validationOf(err);
        if (report) {
            setChecklist((cl) => (cl ? { ...cl, serverReport: report } : cl));
            return;
        }
        setChecklist(null);
        setBanner({ type: 'error', text: err.message || fallback });
    };

    const doSchedule = async (acknowledgedWarnings = []) => {
        if (!scheduledUtc) { setBanner({ type: 'error', text: 'Pick a date and time.' }); return; }
        setBusy(true);
        setBanner(null);
        try {
            const { campaign: c } = await adminApi.scheduleCampaign(campaign.id, { scheduledAt: scheduledUtc.toISOString(), timezone: schedule.timezone, acknowledgedWarnings });
            applyCampaign(c);
            setChecklist(null);
            setBanner({ type: 'success', text: `Scheduled for ${formatDateTime(c.scheduledAt, c.scheduledTimezone)}.` });
        } catch (err) {
            handleRejected(err, 'Could not schedule the campaign.');
        } finally {
            setBusy(false);
        }
    };

    const doSendNow = async (acknowledgedWarnings = []) => {
        setSending(true);
        try {
            const result = await adminApi.sendCampaignNow(campaign.id, { acknowledgedWarnings });
            applyCampaign(result.campaign);
            setChecklist(null);
            setBanner({ type: 'success', text: `Sending started to ${result.recipientEstimate.toLocaleString()} recipients.` });
        } catch (err) {
            handleRejected(err, 'Could not start sending.');
        } finally {
            setSending(false);
        }
    };

    const returnToEditor = () => {
        setChecklist(null);
        setTimeout(focusCtaField, 0);
    };

    const simpleAction = async (fn, successText) => {
        setBusy(true);
        setBanner(null);
        try {
            const result = await fn();
            if (result?.campaign) applyCampaign(result.campaign);
            if (successText) setBanner({ type: 'success', text: successText });
            return result;
        } catch (err) {
            setBanner({ type: 'error', text: err.message || 'Action failed.' });
            return null;
        } finally {
            setBusy(false);
        }
    };

    const duplicate = async () => {
        const result = await simpleAction(() => adminApi.duplicateCampaign(campaign.id));
        if (result?.campaign) navigate(`/admin/campaigns/${result.campaign.id}`);
    };

    if (loading) return <div className={styles.page}><div className={styles.loading}>Loading…</div></div>;
    if (loadError) {
        return (
            <div className={styles.page}>
                <button type="button" className={styles.backLink} onClick={() => navigate('/admin/campaigns')}>← All campaigns</button>
                <div className={styles.errorBanner}>{loadError}</div>
            </div>
        );
    }

    const readiness = campaign?.readiness;
    const testRequired = !campaign || readiness?.testRequired || dirty;
    const complete = Boolean(readiness?.complete) && !dirty;
    // Saved-campaign report from the server; sending is blocked while it has errors.
    const savedBlocking = blockingErrors(campaign?.validation);
    const canSend = Boolean(campaign) && campaign.status === 'DRAFT' && complete && !testRequired && !saving && !savedBlocking.length;
    const savedTest = campaign?.validation?.summary?.test;
    const len = (key) => `${(form[key] || '').length}/${config?.limits?.[key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)] ?? ''}`;

    const header = (
        <div className={styles.header}>
            <div>
                <button type="button" className={styles.backLink} onClick={() => navigate('/admin/campaigns')}>← All campaigns</button>
                <h1 className={styles.pageTitle}>
                    {isNew ? 'New campaign' : (campaign?.name || 'Campaign')}{' '}
                    {campaign && <StatusPill status={campaign.status} />}
                </h1>
                {dirty && <p className={styles.subtitle}>Unsaved changes</p>}
            </div>
            {editable && (
                <div className={styles.actions}>
                    <button type="button" className={styles.secondaryBtn} onClick={() => save()} disabled={saving || (!dirty && !isNew)}>
                        {saving ? 'Saving…' : 'Save Draft'}
                    </button>
                    <button type="button" className={styles.secondaryBtn} onClick={() => setPreview({ id: campaignId, overrides: formToPayload(form) })}>
                        Preview
                    </button>
                </div>
            )}
        </div>
    );

    const bannerEl = banner && (
        <div className={banner.type === 'success' ? styles.successBanner : styles.errorBanner} role={banner.type === 'success' ? 'status' : 'alert'}>
            {banner.text}
        </div>
    );

    if (!editable) {
        return (
            <div className={styles.page}>
                {header}
                {bannerEl}
                <CampaignDetail
                    campaign={campaign}
                    busy={busy}
                    onPreview={() => setPreview({ id: campaign.id })}
                    onDuplicate={duplicate}
                    onUnschedule={() => simpleAction(() => adminApi.unscheduleCampaign(campaign.id), 'Moved back to draft. Send a new test before sending.')}
                    onCancel={() => {
                        if (window.confirm('Cancel this scheduled campaign? It will not be sent.')) {
                            simpleAction(() => adminApi.cancelCampaign(campaign.id), 'Campaign cancelled.');
                        }
                    }}
                />
                {preview && <CampaignPreviewModal campaignId={preview.id} onClose={() => setPreview(null)} />}
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {header}
            {bannerEl}

            <div className={styles.editorGrid}>
                <div>
                    <section className={styles.card}>
                        <h2 className={styles.cardTitle}>Details</h2>
                        <p className={styles.cardHint}>Product &amp; marketing email only — recipients must have product email turned on.</p>
                        <Field
                            label="Campaign type"
                            error={errors.campaignType}
                            help={form.campaignType === 'informational'
                                ? 'Informational: announcements and notices that don\'t promote a page. Sending without a link is allowed.'
                                : 'Promotional: drives subscribers to a page. Needs at least one clickable link before it can be sent.'}
                        >
                            <select className={styles.select} value={form.campaignType} onChange={set('campaignType')} data-testid="campaign-type">
                                {(config?.campaignTypes || ['promotional', 'informational']).map((t) => <option key={t} value={t}>{CAMPAIGN_TYPE_LABELS[t] || t}</option>)}
                            </select>
                        </Field>
                        <Field label="Campaign name" counter={len('name')} error={errors.name} help="Internal only.">
                            <input className={`${styles.input} ${errors.name ? styles.inputError : ''}`} value={form.name} maxLength={150} onChange={set('name')} placeholder="October Horror Starter Pack Launch" />
                        </Field>
                        <Field label="Subject" counter={len('subject')} error={errors.subject}>
                            <input className={`${styles.input} ${errors.subject ? styles.inputError : ''}`} value={form.subject} maxLength={200} onChange={set('subject')} placeholder="Your October horror watchlist starts here 🎃" />
                        </Field>
                        <Field label="Preview text" counter={len('previewText')} error={errors.previewText} help="Shown after the subject in most inboxes. Recommended.">
                            <input className={styles.input} value={form.previewText} maxLength={255} onChange={set('previewText')} placeholder="Eight horror movies to start spooky season." />
                        </Field>
                        <div className={styles.fieldRow}>
                            <Field label="From name" error={errors.fromName}>
                                <input className={styles.input} value={form.fromName} maxLength={80} onChange={set('fromName')} />
                            </Field>
                            <Field
                                label="From email"
                                error={errors.fromEmail}
                                help={config?.fromDomains?.length ? `Must be an address at ${config.fromDomains.join(' or ')}.` : undefined}
                            >
                                <input className={`${styles.input} ${errors.fromEmail ? styles.inputError : ''}`} type="email" value={form.fromEmail} maxLength={255} onChange={set('fromEmail')} placeholder={config?.fromEmail || 'hello@aluvo.app'} />
                            </Field>
                        </div>
                        <Field label="Reply-to" error={errors.replyTo} help="Optional. Must be an Aluvo address.">
                            <input className={styles.input} type="email" value={form.replyTo} onChange={set('replyTo')} placeholder={config?.defaultReplyTo || 'hello@aluvo.app'} />
                        </Field>
                    </section>

                    <section className={styles.card}>
                        <h2 className={styles.cardTitle}>Content</h2>
                        <p className={styles.cardHint}>
                            Plain text — no HTML. Blank line = new paragraph, <code>**bold**</code>, <code>[label](https://aluvo.app/…)</code>.
                            Merge fields: <code>{'{{first_name}}'}</code> (falls back to “there”), <code>{'{{username}}'}</code>.
                        </p>
                        <Field label="Headline" counter={len('headline')} error={errors.headline}>
                            <input className={`${styles.input} ${errors.headline ? styles.inputError : ''}`} value={form.headline} maxLength={200} onChange={set('headline')} placeholder="HORROR SEASON IS HERE 🎃" />
                        </Field>
                        <Field label="Body" counter={len('body')} error={errors.body}>
                            <textarea className={`${styles.textarea} ${errors.body ? styles.inputError : ''}`} rows={8} value={form.body} maxLength={10000} onChange={set('body')} />
                        </Field>

                        <Field group label="Hero image (optional)" error={errors.heroImageUrl} help="JPEG, PNG, WebP or GIF, up to 3 MB. Hosted on Aluvo storage — never embedded.">
                            <div className={styles.actions}>
                                <input ref={fileRef} type="file" accept={HERO_TYPES.join(',')} className={styles.fileInput} onChange={(e) => uploadHero(e.target.files?.[0])} data-testid="hero-file" />
                                <button type="button" className={styles.secondaryBtn} onClick={() => fileRef.current?.click()} disabled={uploading}>
                                    {uploading ? 'Uploading…' : (form.heroImageUrl ? 'Replace image' : 'Upload image')}
                                </button>
                                {form.heroImageUrl && (
                                    <button type="button" className={styles.ghostBtn} onClick={() => setForm((f) => ({ ...f, heroImageUrl: '', heroImageAlt: '' }))}>Remove</button>
                                )}
                            </div>
                            {form.heroImageUrl && <img src={form.heroImageUrl} alt={form.heroImageAlt || ''} className={styles.heroPreview} />}
                        </Field>
                        {form.heroImageUrl && (
                            <Field label="Image alt text" error={errors.heroImageAlt} help="Describes the image for screen readers and when images are blocked.">
                                <input className={styles.input} value={form.heroImageAlt} maxLength={255} onChange={set('heroImageAlt')} placeholder="Horror Starter Pack cover" />
                            </Field>
                        )}
                        {form.heroImageUrl && (
                            <Field label="Link image to URL (optional)" error={errors.heroLinkUrl} help="Makes the hero image clickable. An aluvo.app page; UTM tags are added automatically.">
                                <input
                                    className={`${styles.input} ${errors.heroLinkUrl ? styles.inputError : ''}`}
                                    value={form.heroLinkUrl}
                                    onChange={set('heroLinkUrl')}
                                    placeholder="https://aluvo.app/collections/…"
                                    data-testid="hero-link-url"
                                />
                                {form.campaignType === 'promotional' && !form.heroLinkUrl.trim() && (
                                    <span className={styles.suggestion} data-testid="hero-link-suggestion">{HERO_LINK_SUGGESTION}</span>
                                )}
                            </Field>
                        )}

                        <div className={styles.fieldRow}>
                            <Field label="Button text" error={errors.ctaText}>
                                <input className={styles.input} value={form.ctaText} maxLength={80} onChange={set('ctaText')} placeholder="EXPLORE THE HORROR STARTER PACK" />
                            </Field>
                            <Field label="Button URL" error={errors.ctaUrl} help="An aluvo.app page (e.g. /discover/lists/…, /collections/…, /movie/movie/…). UTM tags are added automatically.">
                                <input id={CTA_URL_INPUT_ID} className={`${styles.input} ${errors.ctaUrl ? styles.inputError : ''}`} value={form.ctaUrl} onChange={set('ctaUrl')} placeholder="https://aluvo.app/discover/lists/horror-starter-pack" />
                            </Field>
                        </div>
                        <Field label="Secondary text (optional)" error={errors.secondaryText}>
                            <textarea className={styles.textarea} rows={3} value={form.secondaryText} maxLength={4000} onChange={set('secondaryText')} />
                        </Field>
                        <Field label="Footer note (optional)" error={errors.footerText} help="The opt-in notice, Manage email preferences and Unsubscribe links are always added.">
                            <input className={styles.input} value={form.footerText} maxLength={500} onChange={set('footerText')} />
                        </Field>
                    </section>

                    <section className={styles.card}>
                        <h2 className={styles.cardTitle}>Audience</h2>
                        <p className={styles.cardHint}>
                            Always limited to <strong>eligible users</strong>: live accounts with a verified email that have product email turned on.
                            Filters combine with AND.
                        </p>
                        <AudienceEditor audience={form.audience} options={config?.audienceOptions} onChange={set('audience')} />
                    </section>
                </div>

                <aside className={styles.sticky}>
                    <div className={styles.card}>
                        <div className={styles.estimate} data-testid="estimate">
                            <div>
                                <div className={styles.estimateLabel}>Estimated recipients</div>
                                <div className={styles.estimateValue}>
                                    {estimate.data ? estimate.data.eligible.toLocaleString() : '…'}
                                </div>
                            </div>
                            {estimate.loading && <span className={styles.muted}>updating…</span>}
                        </div>
                        {estimate.data && estimate.data.excluded > 0 && (
                            <p className={styles.help} style={{ marginTop: 8 }}>
                                {estimate.data.excluded.toLocaleString()} matching account{estimate.data.excluded === 1 ? '' : 's'} excluded (product email off, unverified or invalid address).
                            </p>
                        )}
                        {estimate.error && <p className={styles.fieldError}>{estimate.error}</p>}
                    </div>

                    <div className={styles.testPanel}>
                        <h2 className={styles.cardTitle}>Send test email</h2>
                        <p className={styles.help}>Sends the exact email through the production pipeline so you can check it in your own inbox.</p>
                        <div className={styles.testRecipient}>
                            Test recipient:<br /><strong>{config?.testRecipient || 'No email on your account'}</strong>
                        </div>
                        <button type="button" className={styles.testBtn} onClick={sendTest} disabled={testing || saving || !config?.testRecipient}>
                            {testing ? 'Sending…' : 'Send Test Email'}
                        </button>
                        {testResult?.ok && (
                            <div className={`${styles.testResult} ${styles.testOk}`} role="status">
                                Test email sent to {testResult.to}<br />
                                <span className={styles.muted}>Sent {relativeTime(testResult.at)}</span>
                            </div>
                        )}
                        {testResult && !testResult.ok && (
                            <div className={`${styles.testResult} ${styles.testFail}`} role="alert">{testResult.message}</div>
                        )}
                        {!testResult && campaign?.lastTestSentAt && (
                            <div className={`${styles.testResult} ${styles.muted}`}>
                                Last test {relativeTime(campaign.lastTestSentAt)} to {campaign.lastTestSentTo}
                                {readiness?.testFresh ? '' : ' — content changed since'}
                            </div>
                        )}
                        {campaign && (
                            <div
                                className={`${styles.testResult} ${!dirty && savedTest?.status === 'current' ? styles.testOk : styles.checkWarn}`}
                                data-testid="test-status"
                            >
                                Test status: {dirty ? 'Outdated — you have unsaved changes' : testStatusLabel(savedTest || { status: campaign.lastTestSentAt ? (readiness?.testFresh ? 'current' : 'outdated') : 'none' })}
                            </div>
                        )}
                        <p className={styles.help} style={{ marginTop: 8 }}>
                            A delivered test confirms the email reached your inbox. It doesn&apos;t check links — click each one in the test email.
                        </p>
                    </div>

                    <PreSendChecks state={validation} />

                    <div className={styles.card} id="send">
                        <h2 className={styles.cardTitle}>Schedule or send</h2>
                        {!complete && campaign && !dirty && (
                            <p className={styles.fieldError}>Complete the content first: {Object.values(readiness?.missing || {}).join(' · ')}</p>
                        )}
                        {complete && savedBlocking.length > 0 && (
                            <p className={styles.fieldError} data-testid="send-blocked">Fix the pre-send checks before sending.</p>
                        )}
                        {config?.requireTestBeforeSend && testRequired && (
                            <div className={styles.warningBanner} data-testid="test-required">
                                {dirty ? 'Save your changes, then send yourself a test email before sending this campaign.' : 'Send yourself a test email before sending this campaign.'}
                            </div>
                        )}
                        <div className={styles.fieldRow}>
                            <Field label="Date">
                                <input type="date" className={styles.input} value={schedule.date} onChange={(e) => setSchedule((s) => ({ ...s, date: e.target.value }))} disabled={!canSend} />
                            </Field>
                            <Field label="Time">
                                <input type="time" className={styles.input} value={schedule.time} onChange={(e) => setSchedule((s) => ({ ...s, time: e.target.value }))} disabled={!canSend} />
                            </Field>
                        </div>
                        <Field label="Timezone">
                            <select className={styles.select} value={schedule.timezone} onChange={(e) => setSchedule((s) => ({ ...s, timezone: e.target.value }))} disabled={!canSend}>
                                {[...new Set([browserTimeZone(), ...COMMON_TIME_ZONES])].map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                            </select>
                        </Field>
                        {scheduledUtc && (
                            <p className={styles.help}>
                                {formatDateTime(scheduledUtc, schedule.timezone)} · your time {formatDateTime(scheduledUtc)} · stored as {scheduledUtc.toISOString().replace('.000Z', 'Z')}
                            </p>
                        )}
                        <div className={styles.actions} style={{ marginTop: 8 }}>
                            <button type="button" className={styles.secondaryBtn} onClick={() => setChecklist({ mode: 'schedule', serverReport: null })} disabled={!canSend || busy || !scheduledUtc}>Schedule</button>
                            <button type="button" className={styles.dangerBtn} onClick={() => setChecklist({ mode: 'send', serverReport: null })} disabled={!canSend || busy}>Send Now</button>
                        </div>
                    </div>
                </aside>
            </div>

            {preview && <CampaignPreviewModal campaignId={preview.id} overrides={preview.overrides} onClose={() => setPreview(null)} />}
            {checklist && campaign && (
                <PreSendChecklistModal
                    campaign={campaign}
                    mode={checklist.mode}
                    serverReport={checklist.serverReport}
                    scheduleLabel={scheduledUtc ? formatDateTime(scheduledUtc, schedule.timezone) : ''}
                    busy={checklist.mode === 'send' ? sending : busy}
                    onCancel={() => setChecklist(null)}
                    onConfirm={checklist.mode === 'send' ? doSendNow : doSchedule}
                    onReturnToEditor={returnToEditor}
                />
            )}
        </div>
    );
}
